'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { usePayment, useBooking, useEvent } from '@/store';
import { useVerifyPaymentStatusMutation } from '@/store/checkout/checkout.api';
import useCallApiProcess from '../useCallApiProcess';
import { useTranslation } from 'react-i18next';
import useCheckoutPayment from './useCheckoutPayment';
import useUserTracking from '../userTracking';

export default function useCheckoutValidate3Ds() {
  const [isChallengeOk, setIsChallengeOk] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isWaiting, setIsWaiting] = useState(true);

  const [verifyPaymentStatusRequest, { data: paymentStatus, isLoading }] =
    useVerifyPaymentStatusMutation();

  const {
    booking,
    booking: { order },
  } = useBooking();

  const { returnedFromBank } = useUserTracking();

  const { t } = useTranslation();
  const router = useRouter();
  const { isInChallenge, changeIsInChallengeStatus } = usePayment();

  const {
    event: { gateway },
  } = useEvent();

  const {
    verifyPaymentStatus,
    checkPaymentStatus,
    verifyPaymentStatusResponse,
    confirmChallengeMut,
  } = useCheckoutPayment();

  const { showBackdrop, closeModal } = useCallApiProcess(
    null,
    t('checkout.loadings.processingPayment'),
  );

  /*
   * Confirm challenge
   * @param {string} challengeStatus - The status of the challenge 'completed' | 'aborting'
   *
   */
  const confirmChallenge = async (challengeStatus) => {
    setIsValidating(true);
    showBackdrop();
    await confirmChallengeMut({
      challengeStatus,
      id: router.query.order || order,
      gateway,
    });

    // make a long pulling with challenge evaluation rules
    const payment = await checkPaymentStatus(true); // => completed | notCompleted | need3dsChallenge | 3dsNotCompletedOrFailed

    if (payment.status === 'complete') {
      setIsChallengeOk(true);
    } else {
      setIsChallengeOk(false);
    }

    closeModal();
    setIsValidating(false);
    changeIsInChallengeStatus(false);
    verifyPaymentStatusResponse(payment, 'onConfirm', challengeStatus);
  };

  useEffect(() => {
    if (!router.isReady) return;

    verifyPaymentStatusRequest(router.query?.order || order);
  }, [router.isReady, router.query?.order]);

  useEffect(() => {
    if (paymentStatus) {
      if (paymentStatus.status !== 'paying') {
        returnedFromBank();
      }
      if (paymentStatus.payer_authentication_url?.length > 0) {
        changeIsInChallengeStatus(true);
      } else {
        changeIsInChallengeStatus(false);
        setIsWaiting(false);
      }
    }
  }, [paymentStatus]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (isInChallenge && isWaiting) {
        await confirmChallenge(router.query?.order ? 'complete' : 'aborting');
      }
      setIsWaiting(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [isInChallenge, isWaiting]);

  return {
    isWaiting,
    isChallengeOk,
    isValidating,
  };
}
